import { ref } from 'vue';
import {
  createSessionPdfZipExport,
  downloadSessionPdfZipFile,
  getSessionPdfZipStatus,
  type SessionPdfZipInclude,
  type SessionPdfZipStatus,
} from '@/services/api';
import { ApiError } from '@/services/httpClient';

export interface PdfZipSummaryInput {
  reportCount: number;
  invoiceCount: number;
  hasReceipt: boolean;
  months: number;
}

export function buildPdfZipSummary(input: PdfZipSummaryInput): {
  reports: number;
  invoices: number;
  months: number;
  hasReceipt: boolean;
  canDownload: boolean;
} {
  const reports = Math.max(0, input.reportCount || 0);
  const invoices = Math.max(0, input.invoiceCount || 0);
  const months = Math.max(1, input.months || 12);
  const hasReceipt = Boolean(input.hasReceipt);
  return {
    reports,
    invoices,
    months,
    hasReceipt,
    canDownload: reports > 0 || invoices > 0 || hasReceipt,
  };
}

function triggerBrowserDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'cpt-session-pdfs.zip';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export function useSessionPdfZip() {
  const isRunning = ref(false);
  const progress = ref(0);
  const error = ref<string | null>(null);
  const lastExportId = ref<string | null>(null);
  const manifestSummary = ref<SessionPdfZipStatus['manifest_summary'] | null>(null);

  async function downloadAllPdfs(opts: {
    sessionId: string;
    months?: number;
    lang?: string;
    include?: SessionPdfZipInclude;
    paymentId?: string | null;
    /** VIN -> base64 data-URL photos for report PDFs inside the ZIP */
    vehiclePhotos?: Record<string, string[]>;
    pollMs?: number;
    maxAttempts?: number;
  }): Promise<boolean> {
    if (!opts.sessionId || isRunning.value) return false;
    isRunning.value = true;
    progress.value = 0;
    error.value = null;
    manifestSummary.value = null;

    try {
      const created = await createSessionPdfZipExport(opts.sessionId, {
        months: opts.months ?? 12,
        lang: opts.lang,
        include: opts.include,
        payment_id: opts.paymentId,
        vehiclePhotos: opts.vehiclePhotos,
      });
      lastExportId.value = created.export_id;
      progress.value = created.progress ?? 0;

      if (created.status === 'complete') {
        await finishDownload(opts.sessionId, created);
        return true;
      }

      let pollMs = opts.pollMs ?? created.poll_after_ms ?? 2000;
      const maxAttempts = opts.maxAttempts ?? 150;
      let attempts = 0;
      let status = created;

      while (attempts < maxAttempts) {
        await new Promise((r) => setTimeout(r, pollMs));
        try {
          status = await getSessionPdfZipStatus(opts.sessionId, created.export_id);
        } catch (err) {
          // 429: status poll budget exhausted (or shared session rate limit) —
          // back off and keep polling instead of failing the whole download.
          if (err instanceof ApiError && err.status === 429) {
            pollMs = Math.min(pollMs * 2, 15000);
            attempts += 1;
            continue;
          }
          throw err;
        }
        progress.value = status.progress ?? progress.value;
        if (status.status === 'complete') {
          await finishDownload(opts.sessionId, status);
          return true;
        }
        if (status.status === 'failed') {
          error.value = status.error || 'Could not prepare the ZIP. Please try again.';
          return false;
        }
        attempts += 1;
      }

      error.value = 'Could not prepare the ZIP. Please try again.';
      return false;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Could not prepare the ZIP. Please try again.';
      return false;
    } finally {
      isRunning.value = false;
    }
  }

  async function finishDownload(sessionId: string, status: SessionPdfZipStatus) {
    progress.value = 100;
    manifestSummary.value = status.manifest_summary || null;
    const filename = status.filename || 'cpt-session-pdfs.zip';

    if (status.download_url) {
      // Prefer navigable signed URL when present; still use anchor for download attr UX.
      const link = document.createElement('a');
      link.href = status.download_url;
      link.download = filename;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
      return;
    }

    const blob = await downloadSessionPdfZipFile(sessionId, status.export_id);
    triggerBrowserDownload(blob, filename);
  }

  return {
    isRunning,
    progress,
    error,
    lastExportId,
    manifestSummary,
    downloadAllPdfs,
    buildPdfZipSummary,
  };
}
